/**
 * XML Block Importer Utility
 * Parses XML files and extracts block information for importing as standalone blocks
 */

/**
 * Decode HTML entities in a string
 * @param {string} html - HTML encoded string
 * @returns {string} Decoded HTML string
 */
export const decodeHtmlEntities = (html) => {
  if (!html || html === '?' || html.trim() === '') {
    return null;
  }
  
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

/**
 * Parse XML string and extract all block elements
 * @param {string} xmlString - XML content as string
 * @returns {Array} Array of block objects with metadata and language content
 */
export const parseXMLBlocks = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Invalid XML format');
    }
    
    const blocks = [];
    const blockElements = xmlDoc.querySelectorAll('liveBlocks > block');
    
    blockElements.forEach((blockElement) => {
      const getTextContent = (tagName) => {
        const element = blockElement.querySelector(tagName);
        return element ? element.textContent || '' : '';
      };
      
      const block = {
        author: getTextContent('author'),
        date: getTextContent('date'),
        time: getTextContent('time'),
        filename: getTextContent('filename'),
        nameFr: getTextContent('nameFr'),
        nameNl: getTextContent('nameNl'),
        povc: getTextContent('povc'),
        category: getTextContent('category'),
        iytxtbs: getTextContent('iytxtbs'),
        status: getTextContent('status'),
        langFr: getTextContent('langFr'),
        langNl: getTextContent('langNl'),
        langEn: getTextContent('langEn'),
        langDe: getTextContent('langDe'),
      };
      
      blocks.push(block);
    });
    
    return blocks;
  } catch (error) {
    console.error('Error parsing XML:', error);
    throw error;
  }
};

/**
 * Sanitize block name: keep only letters, digits, hyphen, underscore, and brackets.
 * All other characters are replaced with empty string.
 * @param {string} name - Raw block name
 * @returns {string} Sanitized name, or 'Unnamed Block' if result is empty
 */
export const sanitizeBlockName = (name) => {
  if (!name || typeof name !== 'string') {
    return 'Unnamed Block';
  }
  const sanitized = name.replace(/[^a-zA-Z0-9\-_()[\]]/g, ' ').trim();
  return sanitized === '' ? 'Unnamed Block' : sanitized;
};

/**
 * Get block name for standalone block (sanitized)
 * @param {Object} block - Block object from XML
 * @returns {string} Block name
 */
export const getBlockName = (block) => {
  let raw = 'Unnamed Block';
  if (block.iytxtbs && block.iytxtbs.trim() !== '') {
    raw = block.iytxtbs.trim();
  } else if (block.nameFr && block.nameFr.trim() !== '') {
    raw = block.nameFr.trim();
  } else if (block.nameNl && block.nameNl.trim() !== '') {
    raw = block.nameNl.trim();
  }
  return sanitizeBlockName(raw);
};

/**
 * Get available languages for a block
 * @param {Object} block - Block object from XML
 * @returns {Array} Array of language codes that have valid content
 */
export const getAvailableLanguages = (block) => {
  const languages = [];
  
  const langMap = {
    fr: block.langFr,
    nl: block.langNl,
    en: block.langEn,
    de: block.langDe,
  };
  
  Object.keys(langMap).forEach((lang) => {
    const content = langMap[lang];
    if (content && content.trim() !== '' && content.trim() !== '?') {
      languages.push(lang);
    }
  });
  
  return languages;
};

/**
 * Get HTML content for a specific language
 * @param {Object} block - Block object from XML
 * @param {string} language - Language code (fr, nl, en, de)
 * @returns {string|null} Decoded HTML content or null if not available
 */
export const getLanguageContent = (block, language) => {
  const langMap = {
    fr: block.langFr,
    nl: block.langNl,
    en: block.langEn,
    de: block.langDe,
  };
  
  const content = langMap[language];
  if (!content || content.trim() === '' || content.trim() === '?') {
    return null;
  }
  
  return decodeHtmlEntities(content);
};

/**
 * Escape text for use inside an XML element
 * @param {string} text - Raw text
 * @returns {string} XML-escaped text
 */
const escapeXmlText = (text) => {
  if (text == null || typeof text !== 'string') {
    return '';
  }
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const BLOCK_TAGS = [
  'author', 'date', 'time', 'filename', 'nameFr', 'nameNl',
  'povc', 'category', 'iytxtbs', 'status',
  'langFr', 'langNl', 'langEn', 'langDe',
];

/**
 * Export an array of block objects to XML in the same format as the input
 * @param {Array<Object>} blocks - Array of block objects (author, date, time, filename, nameFr, nameNl, povc, category, iytxtbs, status, langFr, langNl, langEn, langDe)
 * @returns {string} Full XML document string
 */
export const exportBlocksToXML = (blocks) => {
  const declaration = '<?xml version="1.0" encoding="utf-8"?>';
  const rootOpen = '<livedocsReport xmlns:xs="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:sld="http://www.inventivedesigners.com/livedocs">';
  const rootClose = '</livedocsReport>';
  const liveBlocksOpen = '\n   <liveBlocks>';
  const liveBlocksClose = '\n   </liveBlocks>';

  const blockXml = (block) => {
    const parts = [];
    for (const tag of BLOCK_TAGS) {
      const value = block[tag];
      const escaped = escapeXmlText(value);
      if (escaped === '') {
        parts.push(`\n         <${tag}/>`);
      } else {
        parts.push(`\n         <${tag}>${escaped}</${tag}>`);
      }
    }
    return `\n      <block>${parts.join('')}\n      </block>`;
  };

  const blocksXml = Array.isArray(blocks) ? blocks.map(blockXml).join('') : '';
  return `${declaration}\n${rootOpen}${liveBlocksOpen}${blocksXml}${liveBlocksClose}\n${rootClose}`;
};


